/** @jsxImportSource @emotion/react */

'use client';

import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import ErrorBoundary from '@campaign-manager/feature/feature/error/ErrorBoundary';

import * as styles from './styles';

export interface WizardLayoutProps {
  /** Header component (WizardHeader with title and actions) */
  header: ReactNode;
  /** Left sidebar content (typically WizardStepper) */
  sidebar: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Optional right drawer for context info (e.g., ForecastingVisor) */
  contextDrawer?: ReactNode;
  /** Fallback component for error boundary */
  errorFallback?: ReactNode;
}

/**
 * WizardLayout - A CSS Grid-based layout for wizard flows
 *
 * This layout provides explicit grid areas for:
 * - Header: Top row with title and action buttons (Linear/Stripe pattern)
 * - Sidebar: Left column for stepper navigation
 * - Main: Center area for step content
 * - Context Drawer: Optional right column for contextual info (full height)
 *
 * Layout properties:
 * - No magic padding values (pb: 14, paddingTop: 92px, etc.)
 * - No position: fixed/absolute/sticky - all layout via CSS Grid
 * - Explicit grid areas make the layout predictable
 * - Context drawer is a proper grid column, not an absolutely positioned overlay
 * - Actions in header keeps them close to content (modern SaaS pattern)
 */
export default function WizardLayout({
  header,
  sidebar,
  children,
  contextDrawer,
  errorFallback,
}: WizardLayoutProps) {
  const hasContextDrawer = Boolean(contextDrawer);

  return (
    <Box css={hasContextDrawer ? styles.wizardLayoutRootWithContext : styles.wizardLayoutRoot}>
      {/* Header Area */}
      <Box css={styles.headerArea}>{header}</Box>

      {/* Sidebar Area */}
      <Box css={styles.sidebarArea}>{sidebar}</Box>

      {/* Main Content Area */}
      <Box css={styles.mainArea}>
        {errorFallback ? (
          <ErrorBoundary fallback={errorFallback}>{children}</ErrorBoundary>
        ) : (
          children
        )}
      </Box>

      {/* Context Drawer Area (optional) */}
      {hasContextDrawer && <Box css={styles.contextArea}>{contextDrawer}</Box>}
    </Box>
  );
}

// Re-export styles for consumers who need direct access to values
export {
  WIZARD_CONTEXT_DRAWER_WIDTH,
  WIZARD_HEADER_HEIGHT,
  WIZARD_SIDEBAR_WIDTH,
} from './styles';
